import React, {useCallback, useState} from 'react';

import {Box, Button, TextField, Typography} from '@mui/material';

import {COLORS} from '@/styles/colors';

export interface NumericInputOptionsColorsProps {
  backgroundColor: string;
  backgroundColorHover: string;
  colorText: string;
}

interface NumericInputProps {
  options: number[];
  value: number;
  onChange: (value: number, prefill: boolean) => void;
  maxAmount?: number;
  optionsColors?: NumericInputOptionsColorsProps;
}

const REGEX_NUMBER_WITH_DECIMALS: RegExp = /\B(?=(\d{3})+(?!\d))/g;

const REGEX_NUMERIC_DECIMALS: RegExp = /^\d*\.?\d{0,2}$/;

const formatNumberWithCommas = (number: string) => {
  const [integerPart, decimalPart] = number.split('.');
  const formattedIntegerPart = integerPart.replace(
    REGEX_NUMBER_WITH_DECIMALS,
    ',',
  );
  return decimalPart
    ? `${formattedIntegerPart}.${decimalPart}`
    : `${formattedIntegerPart}.00`;
};

export const NumericInput: React.FC<NumericInputProps> = ({
  options,
  value,
  onChange,
  maxAmount = 200,
  optionsColors = {
    backgroundColor: COLORS.PASTEL_E,
    backgroundColorHover: COLORS.PASTEL_E_HOVER,
    colorText: COLORS.ON_SURFACE_1,
  },
}) => {
  const [inputValue, setInputValue] = useState<string>(
    formatNumberWithCommas(value.toFixed(2)),
  );
  const [isError, setIsError] = useState<boolean>(value > maxAmount);

  const handlePresetClick = useCallback(
    (amount: number) => {
      setInputValue(formatNumberWithCommas(amount.toFixed(2)));
      onChange(amount, true);
      setIsError(amount > maxAmount);
    },
    [maxAmount, onChange],
  );

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = event.target.value.replace('$', '').replace(/,/g, '');

      if (REGEX_NUMERIC_DECIMALS.test(rawValue) || rawValue === '') {
        setInputValue(rawValue);

        const numericValue = parseFloat(rawValue);
        const validValue = !isNaN(numericValue) ? numericValue : 0;
        setIsError(validValue > maxAmount);
        onChange(validValue, false);
      }
    },
    [maxAmount, onChange],
  );

  const handleBlur = useCallback(() => {
    const numericValue = parseFloat(inputValue.replace(/,/g, ''));
    if (!isNaN(numericValue)) {
      const formatted = formatNumberWithCommas(numericValue.toFixed(2));
      setInputValue(formatted);
      setIsError(numericValue > maxAmount);
    } else {
      setInputValue(formatNumberWithCommas('0.00'));
      setIsError(false);
    }
  }, [inputValue, maxAmount]);

  return (
    <Box
      id="numeric-input-with-options"
      display="flex"
      flexDirection="column"
      alignItems="center"
      gap={2}
      width="100%">
      <TextField
        id="numeric-input-text"
        value={`$${inputValue}`}
        onChange={handleInputChange}
        onBlur={handleBlur}
        variant="outlined"
        type="text"
        error={isError}
        inputProps={{
          inputMode: 'numeric',
          style: {
            textAlign: 'center',
            fontSize: '48px',
            fontWeight: 500,
            lineHeight: '56px',
            letterSpacing: '-2.5%',
            color: COLORS.ON_SURFACE,
          },
        }}
        sx={{
          width: '100%',
          height: '88px',
          '& .MuiOutlinedInput-root': {
            backgroundColor: COLORS.WHITE,
            borderRadius: '12px',
            height: '88px',
            alignItems: 'center',
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: COLORS.ON_SURFACE_1,
            },
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: isError ? COLORS.RED_CINNABAR : undefined,
          },
          '& .MuiInputBase-input': {
            textAlign: 'center',
            fontSize: '48px',
            fontWeight: 500,
            lineHeight: '56px',
            letterSpacing: '-2.5%',
            color: COLORS.ON_SURFACE,
          },
        }}
      />

      <Box
        display="flex"
        width="100%"
        sx={{justifyContent: 'center', gap: '12px'}}>
        {options.map(option => (
          <Button
            key={option}
            variant="contained"
            onClick={() => handlePresetClick(option)}
            sx={{
              backgroundColor: optionsColors.backgroundColor,
              color: optionsColors.colorText,
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '20px',
              letterSpacing: 0.14,
              borderRadius: '100px',
              padding: '6px 12px',
              '&:hover': {
                backgroundColor: optionsColors.backgroundColorHover,
              },
              width: '76px',
              height: '32px',
            }}>
            ${option}
          </Button>
        ))}
      </Box>
      {isError && (
        <Typography
          sx={{
            color: COLORS.RED_CINNABAR,
            fontSize: '12px',
            lineHeight: '14px',
            textAlign: 'center',
          }}>
          For security reasons, maximum amount is ${maxAmount}
        </Typography>
      )}
    </Box>
  );
};
